import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";

export interface DueSaleInterface {
  sale_id: number;
  sale_number: number;
  financial_entry_id: number;
  amount: string | number;
  due_date: string;
  status: "PENDING" | "OVERDUE" | string;
  client_name: string | null;
}

// Confirmar recebimento é ação de quem administra o caixa — mesmo grupo que
// já enxerga o sino de mensalidade.
const PROFILES_WITH_NOTICE = ["ADMIN", "OWNER", "MANAGER"];

type SaleDueContextValue = {
  dueSales: DueSaleInterface[];
  total: number;
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  refresh: () => void;
};

const EMPTY: SaleDueContextValue = {
  dueSales: [],
  total: 0,
  modalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  refresh: () => {}
};

const SaleDueContext = createContext<SaleDueContextValue>(EMPTY);

/** Seguro fora do provider: devolve o estado vazio em vez de estourar. */
export const useSaleDue = () => useContext(SaleDueContext);

export const SaleDueProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { userProfile, isAuthChecked } = useAuth();
  const [dueSales, setDueSales] = useState<DueSaleInterface[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const check = useCallback(() => {
    if (!isAuthChecked || !userProfile) return;
    if (!PROFILES_WITH_NOTICE.includes(userProfile)) return;

    api
      .get<{ total: number; sales: DueSaleInterface[] }>("/sales/check-due")
      .then((res) => setDueSales(res.data.sales ?? []))
      .catch((error: any) => {
        // Endpoint indisponível (404) ou outro erro: falha silenciosa, não
        // interrompe o uso do PDV por causa do sino.
        if (error.response?.status !== 404) {
          console.error("Erro ao checar vendas a prazo vencendo:", error.response?.data || error);
        }
        setDueSales([]);
      });
  }, [isAuthChecked, userProfile]);

  useEffect(() => {
    check();
    const iv = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [check]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const value = useMemo<SaleDueContextValue>(
    () => ({
      dueSales,
      total: dueSales.length,
      modalOpen,
      openModal,
      closeModal,
      refresh: check
    }),
    [dueSales, modalOpen, openModal, closeModal, check]
  );

  return <SaleDueContext.Provider value={value}>{children}</SaleDueContext.Provider>;
};
